import { useMemo } from 'react';
import type { ApexOptions } from 'apexcharts';

import { DEFAULT_COLOR, DEFAULT_DECIMALS, DEFAULT_LINE_COLORS } from '../constants';

import type { StanLineChartProps, UseStanLineChartOptionsResult } from '../types';

import { abbreviateNumber, buildStackedPalette } from '../../chart';
import { useApexChart } from '@/hooks/useApexChart';
import styles from '../StanLineChart.module.css';

/* ---------------------------------- hook ---------------------------------- */

export function useStanLineChartOptions({
    series = [],
    customOptions = {},
    chartPrefix = '',
    dataSuffix = '',
    decimals,
    type,
    stacked = false,
    tooltipFormatter,
    showStanBrandingInTooltip,
    categories,
    enableZoom,
    yAxisMin,
    yAxisMax,
    stroke,
    markers,
}: StanLineChartProps): UseStanLineChartOptionsResult {
    // 1) Resolve once, memoize small primitives
    const resolved = useMemo(() => {
        const isLine = type === 'line';
        const chartType: NonNullable<ApexOptions['chart']>['type'] = type ?? 'line';
        const resolvedDecimals = decimals ?? (isLine ? 0 : DEFAULT_DECIMALS);
        const showBranding = showStanBrandingInTooltip ?? !isLine; // brand area by default
        const zoomEnabled = enableZoom ?? isLine;

        // Build color palette
        const resolvedSeries = series;
        const seriesPalette = resolvedSeries
            .map(e => (e && typeof e === 'object' && e.color ? e.color : DEFAULT_COLOR))
            .filter((v): v is string => Boolean(v));

        let palette: string[];
        if (seriesPalette.length) {
            palette = seriesPalette;
        } else if (chartType === 'area') {
            const base = seriesPalette[0] ?? DEFAULT_COLOR;
            palette = stacked ? buildStackedPalette(base) : [base];
        } else {
            palette = DEFAULT_LINE_COLORS;
        }

        // Value formatting: line = precise, area = abbreviated
        const formatValue =
            tooltipFormatter ??
            (isLine
                ? (val: number | null) => `${chartPrefix}${(val ?? 0).toFixed(resolvedDecimals)}${dataSuffix}`
                : (val: number | null) => `${chartPrefix}${abbreviateNumber(val ?? 0, resolvedDecimals)}${dataSuffix}`);

        return {
            resolvedDecimals,
            chartType,
            showBranding,
            zoomEnabled,
            palette,
            formatValue,
        };
    }, [
        decimals,
        type,
        series,
        showStanBrandingInTooltip,
        enableZoom,
        stacked,
        tooltipFormatter,
        chartPrefix,
        dataSuffix,
    ]);

    // 3) Hand off to shared apex hook
    const variantName: 'line' | 'area' = resolved.chartType === 'area' ? 'area' : 'line';
    const result = useApexChart({
        variant: variantName,
        series,
        customOptions,
        chartPrefix,
        dataSuffix,
        decimals: resolved.resolvedDecimals,
        stacked,
        tooltipFormatter: resolved.formatValue,
        showStanBrandingInTooltip: resolved.showBranding,
        includeTooltipMarker: variantName === 'line',
        categories,
        yAxisMin,
        yAxisMax,
        paletteConfig: {
            defaultColors: DEFAULT_LINE_COLORS,
            defaultColor: DEFAULT_COLOR,
            seriesColors: resolved.palette,
        },
        enableZoom,
        stroke,
        markers,
        brandedTooltipClassName: styles.brandedTooltip || '',
    });

    return {
        ...result,
        chartType: resolved.chartType as 'line' | 'area',
    };
}
